import moment from 'moment'
import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { getRangeFromDateFilter } from 'utils/dateUtils'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from '../../PartnerOptions/constants'
import { CAMPAIGN_TYPES_VALUES } from '../constants'
import { AUTOMATED_LEVEL_BASED_TRAINING } from '../WizardCampaign/steps/TrainingModuleStep/constants'
import { DEFAULT_SIMULATION_PROPERTIES } from '../WizardCampaign/WizardSimulationCampaign/constants'
import { DEFAULT_TRAINING_PROPERTIES } from '../WizardCampaign/WizardTrainingCampaign/constants'

import { EMERGENCY_STOP_REQUEST_ASYNC } from './CampaignReport/constants'
import {
  cancelCampaignError,
  cancelCampaignSuccess,
  copyCampaignError,
  copyCampaignSuccess,
  deleteCampaignError,
  deleteCampaignSuccess,
  endCampaignError,
  endCampaignSuccess,
  fetchCampaignsError,
  fetchCampaignsSuccess,
  getRecommendedCampaignsError,
  getRecommendedCampaignsSuccess,
  saveDraftError,
  saveDraftSuccess,
} from './actions'
import {
  CAMPAIGN_TYPE_FILTER_CHANGE,
  CANCEL_CAMPAIGN_ASYNC,
  COPY_CAMPAIGN_ASYNC,
  DATA_REQUEST_ASYNC,
  DATE_FILTER_CHANGE,
  DELETE_CAMPAIGN_ASYNC,
  END_CAMPAIGN_ASYNC,
  GET_RECOMMENDED_CAMPAIGNS,
  LANGUAGES_FILTER_CHANGE,
  PAGINATE_CHANGE,
  SAVE_DRAFT_ASYNC,
  SEARCH_CHANGE,
  SORT_PROPERTY_CHANGE,
  STATUS_FILTER_CHANGE,
} from './constants'
import {
  selectCampaignsSearchFilter,
  selectCampaignTypeFilter,
  selectDateFilter,
  selectLanguagesFilter,
  selectPage,
  selectSortProperty,
  selectStatusFilter,
} from './selectors'

const getCampaignDraft = (campaignData) => {
  const CAMPAIGNS_MAP = {
    [CAMPAIGN_TYPES_VALUES.SIMULATION]: {
      endDate: moment(campaignData.endDate).format('YYYY-MM-DD'),
      closeTime: moment(campaignData.closeTime).format('YYYY-MM-DDTHH:mm[Z]'),
      sendRemindersUntil: new Date(campaignData.closeTime).toISOString(),
      scenariosIds: [campaignData.data.scenario?.id],
      landingPageId: campaignData.data.landingPageId,
      ...DEFAULT_SIMULATION_PROPERTIES,
    },
    [CAMPAIGN_TYPES_VALUES.TRAINING]: {
      trainingCampaignData: {
        subject: campaignData.data.subject,
        message: campaignData.data.message,
        fromEmail: campaignData.data.fromEmail,
      },
      closeTime: moment(campaignData.endDate).format('YYYY-MM-DDTHH:mm[Z]'),
      sendRemindersUntil: new Date(campaignData.endDate).toISOString(),
      ...DEFAULT_TRAINING_PROPERTIES,
    },
  }

  return CAMPAIGNS_MAP[campaignData.flowType]
}

const fetchCampaignsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      DATA_REQUEST_ASYNC.INITIATE,
      CAMPAIGN_TYPE_FILTER_CHANGE,
      LANGUAGES_FILTER_CHANGE,
      STATUS_FILTER_CHANGE,
      DATE_FILTER_CHANGE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      PAGINATE_CHANGE,
      SEARCH_CHANGE,
      COPY_CAMPAIGN_ASYNC.SUCCESS,
      CANCEL_CAMPAIGN_ASYNC.SUCCESS,
      END_CAMPAIGN_ASYNC.SUCCESS,
      DELETE_CAMPAIGN_ASYNC.SUCCESS,
      EMERGENCY_STOP_REQUEST_ASYNC.SUCCESS,
      SORT_PROPERTY_CHANGE
    )
    .filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.awarenessCenter.campaigns)
      return true
    })
    .mergeMap(() => {
      const state = store.getState()
      return api.awarenessCenter
        .getCampaigns(
          selectPage(state) || 1,
          selectSortProperty(state),
          selectCampaignTypeFilter(state)?.map(({ value }) => Number(value)),
          selectLanguagesFilter(state)?.map(({ value }) => Number(value)),
          selectStatusFilter(state)?.map(({ value }) => Number(value)),
          getRangeFromDateFilter(selectDateFilter(state)),
          selectCampaignsSearchFilter(state),
          store.getState().partnerOptions.selectedCompany?.value,
          !!store.getState().app.data.partner
        )
        .mergeMap((response) => Observable.of(fetchCampaignsSuccess(response.response)))
        .catch((error) => Observable.of(epicsErrorHandler(fetchCampaignsError, error)))
        .takeUntil(action$.ofType(DATA_REQUEST_ASYNC.CANCEL))
    })

const copyCampaignEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(COPY_CAMPAIGN_ASYNC.INITIATE).mergeMap((action) =>
    api.awarenessCenter
      .copyCampaign(action.payload.id, action.payload.companiesIds)
      .map((response) => copyCampaignSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(copyCampaignError, error)))
      .takeUntil(action$.ofType(COPY_CAMPAIGN_ASYNC.CANCEL))
  )

const cancelCampaignEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(CANCEL_CAMPAIGN_ASYNC.INITIATE).mergeMap((action) =>
    api.awarenessCenter
      .cancelCampaign(action.payload.id)
      .map((response) => cancelCampaignSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(cancelCampaignError, error)))
      .takeUntil(action$.ofType(CANCEL_CAMPAIGN_ASYNC.CANCEL))
  )

const endCampaignEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(END_CAMPAIGN_ASYNC.INITIATE).mergeMap((action) =>
    api.awarenessCenter
      .endCampaign(action.payload.id)
      .map((response) => endCampaignSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(endCampaignError, error)))
      .takeUntil(action$.ofType(END_CAMPAIGN_ASYNC.CANCEL))
  )

const deleteCampaignEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(DELETE_CAMPAIGN_ASYNC.INITIATE).mergeMap((action) =>
    api.awarenessCenter
      .deleteCampaign(action.payload.id)
      .map((response) => deleteCampaignSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(deleteCampaignError, error)))
      .takeUntil(action$.ofType(DELETE_CAMPAIGN_ASYNC.CANCEL))
  )

const getRecommendedCampaignsEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(GET_RECOMMENDED_CAMPAIGNS.INITIATE, FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
    .filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.awarenessCenter.campaigns)
      return true
    })
    .mergeMap(() =>
      api.awarenessCenter
        .getRecommendedCampaigns(
          store.getState().partnerOptions.selectedCompany?.value,
          !!store.getState().app.data.partner
        )
        .map((response) => getRecommendedCampaignsSuccess(response))
        .catch((error) => Observable.of(epicsErrorHandler(getRecommendedCampaignsError, error)))
        .takeUntil(action$.ofType(GET_RECOMMENDED_CAMPAIGNS.CANCEL))
    )

const createRecommendedCampaignEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(SAVE_DRAFT_ASYNC.INITIATE).mergeMap((action) => {
    const campaignData = store.getState().awarenessCenter.campaigns.recommendedPreviewData
    const participantsChoices = {}
    const trainingIds =
      campaignData.trainingVideo.id === AUTOMATED_LEVEL_BASED_TRAINING.id
        ? []
        : [campaignData.trainingVideo.id]

    campaignData.featuredGroups.forEach((group) => {
      participantsChoices[group] = [group]
    })

    return api.wizardCampaign
      .saveCampaign(store.getState().partnerOptions.selectedCompany?.value, {
        campaignTemplateId: action.payload.id,
        defaultLocaleId: action.payload.defaultLocaleId,
        participantsChoices,
        flowType: campaignData.flowType,
        name: campaignData.name,
        deliveryType: campaignData.deliveryType,
        participantsExcludeChoicesAutoFill: true,
        scheduleTime: moment(campaignData.launchDate).format('YYYY-MM-DDTHH:mm'),
        trainingIds,
        trainingReminders: [],
        ...getCampaignDraft(campaignData),
        sendManagerReportEveryXDays: null,
      })
      .map(({ response }) => saveDraftSuccess(response.id))
      .catch((error) => Observable.of(epicsErrorHandler(saveDraftError, error)))
      .takeUntil(action$.ofType(SAVE_DRAFT_ASYNC.CANCEL))
  })

export default combineEpics(
  fetchCampaignsEpic,
  copyCampaignEpic,
  cancelCampaignEpic,
  endCampaignEpic,
  deleteCampaignEpic,
  getRecommendedCampaignsEpic,
  createRecommendedCampaignEpic
)
